import React from "react"
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Chip,
  Box,
  Container,
  Fade,
  Paper,
  Card,
  CardContent,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  LinearProgress,
  Button,
  CircularProgress,
} from "@mui/material"
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ThumbUp as ThumbUpIcon,
  Build as BuildIcon,
  Book as BookIcon,
  UploadFile as UploadFileIcon,
  ThumbDown,
} from "@mui/icons-material"

interface FormErrors {
  feedbackType?: string
  feedbackCategory?: string
  feedbackText?: string
  [key: string]: string | undefined
}

interface JournalFormProps {
  theme: any
  navigate: (path: string) => void
  feedbackType: string
  setFeedbackType: (value: string) => void
  feedbackCategory: string
  setFeedbackCategory: (value: string) => void
  feedbackDate: string
  setFeedbackDate: (value: string) => void
  feedbackText: string
  setFeedbackText: (value: string) => void
  errors: FormErrors
  loading: boolean
  handleBackToList: () => void
  handleSave: (formData: FormData) => void
  characterCount: number
  minCharacters: number
  progressValue: number
  isEdit?: boolean
  existingFiles?: string[]
}

const JournalForm: React.FC<JournalFormProps> = ({
  theme,
  navigate,
  feedbackType,
  setFeedbackType,
  feedbackCategory,
  setFeedbackCategory,
  feedbackDate,
  setFeedbackDate,
  feedbackText,
  setFeedbackText,
  errors,
  loading,
  handleBackToList,
  handleSave,
  characterCount,
  minCharacters,
  progressValue,
  isEdit = false,
  existingFiles = [], // new prop for edit mode
}) => {
  const [attachments, setAttachments] = React.useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = React.useState<string[]>(existingFiles);
  const [removedFiles, setRemovedFiles] = React.useState<string[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      // Only allow up to 5 files, and only allowed types
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
      ];
      const files = Array.from(e.target.files).filter(
        (file) => allowedTypes.includes(file.type)
      ).slice(0, 5);
      setAttachments(prev => [...prev, ...files].slice(0, 5));
    }
  };

  const handleRemoveAttachment = (idx: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveExistingAttachment = (idx: number) => {
    setRemovedFiles(prev => [...prev, existingAttachments[idx]]);
    setExistingAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveClick = () => {
    // Build FormData for file upload
    const formData = new FormData();
    formData.append('dateOfJournal', feedbackDate);
    formData.append('feedbackType', feedbackType);
    formData.append('feedbackCategory', feedbackCategory);
    formData.append('employeeFeedback', feedbackText);
    // Optionally add tlFeedback if you have it in the form
    // formData.append('tlFeedback', tlFeedback || '');
    attachments.forEach(file => {
      formData.append('attachments', file);
    });
    if (removedFiles.length > 0) {
      formData.append('removedFiles', JSON.stringify(removedFiles));
    }
    handleSave(formData);
  };

  return (
    <>
      {/* Header */}
      <AppBar position="fixed" elevation={0} sx={{ background: `linear-gradient(135deg,${theme.palette.primary.main},${theme.palette.primary.dark})` }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate("/")}>
            <ArrowBackIcon />
          </IconButton>
          <BookIcon sx={{ mr: 2, color: theme.palette.secondary.contrastText }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, color: theme.palette.secondary.contrastText }}>
            Employee Journal
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box
        sx={{
          pt: 10,
          pb: 4,
          px: 2,
          minHeight: "100vh",
          backgroundColor: "#F8FAF6",
        }}
      >
        <Container maxWidth="md">
          <Fade in timeout={800}>
            <Box>
              {/* Header Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  mb: 3,
                  background: "linear-gradient(135deg, rgba(122, 158, 62, 0.08) 0%, rgba(91, 138, 114, 0.08) 100%)",
                  border: "1px solid rgba(122, 158, 62, 0.2)",
                  borderRadius: 3,
                  textAlign: "center",
                }}
              >
                <Typography variant="h4" gutterBottom sx={{ color: "primary.main" }}>
                  Share Your Feedback
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Your insights help us grow together. Please provide detailed feedback to make a meaningful impact.
                </Typography>
              </Paper>

              {/* Form Card */}
              <Card sx={{ overflow: "visible" }}>
                <CardContent sx={{ p: 4 }}>
                  <Stack spacing={3}>
                    {/* Type and Category Row - Using Box instead of Grid */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 3,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <FormControl fullWidth error={!!errors.feedbackType}>
                          <InputLabel>Feedback Type</InputLabel>
                          <Select
                            value={feedbackType}
                            onChange={(e) => {
                              setFeedbackType(e.target.value)
                            }}
                            label="Feedback Type"
                          >
                            <MenuItem value="Positive">
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <ThumbUpIcon sx={{ color: "#4CAF50" }} fontSize="small" />
                                <span>Positive</span>
                              </Stack>
                            </MenuItem>
                            <MenuItem value="Negative">
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <ThumbDown sx={{ color: "#FF9800" }} fontSize="small" />
                                <span>Negative</span>
                              </Stack>
                            </MenuItem>
                          </Select>
                          {errors.feedbackType && (
                            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                              {errors.feedbackType}
                            </Typography>
                          )}
                        </FormControl>
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <FormControl fullWidth error={!!errors.feedbackCategory}>
                          <InputLabel>Feedback Category</InputLabel>
                          <Select
                            value={feedbackCategory}
                            onChange={(e) => {
                              setFeedbackCategory(e.target.value)
                            }}
                            label="Feedback Category"
                          >
                            <MenuItem value="Technical">Technical</MenuItem>
                            <MenuItem value="Non-Technical">Non-Technical</MenuItem>
                          </Select>
                          {errors.feedbackCategory && (
                            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                              {errors.feedbackCategory}
                            </Typography>
                          )}
                        </FormControl>
                      </Box>
                    </Box>

                    {/* Date Field */}
                    <TextField
                      type="date"
                      label="Feedback Date"
                      value={feedbackDate}
                      onChange={(e) => setFeedbackDate(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />

                    <Divider />

                    {/* Feedback Text Area */}
                    <Box>
                      <TextField
                        label="Employee Feedback"
                        multiline
                        rows={8}
                        fullWidth
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        error={!!errors.feedbackText}
                        helperText={errors.feedbackText}
                        placeholder="Share your detailed feedback here. Be specific about what went well or what could be improved..."
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontSize: "1.1rem",
                            lineHeight: 1.6,
                          },
                        }}
                      />

                      {/* Character Counter */}
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={progressValue}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: "rgba(122, 158, 62, 0.1)",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: characterCount >= minCharacters ? "#4CAF50" : "#FF9800",
                                borderRadius: 4,
                              },
                            }}
                          />
                        </Box>
                        <Chip
                          label={`${characterCount}/${minCharacters}`}
                          size="small"
                          color={characterCount >= minCharacters ? "success" : "warning"}
                          sx={{ fontWeight: 600, minWidth: "100px" }}
                        />
                      </Stack>
                    </Box>

                    {/* Attachments */}
                    <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#fafafa' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Attach Files (Max 5)
                      </Typography>

                      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                        Supported formats: PDF, Word (.doc, .docx), JPG, PNG
                      </Typography>

                      <label htmlFor="file-upload" style={{ cursor: loading ? 'not-allowed' : 'pointer' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: '#e3f2fd',
                            borderRadius: 1,
                            px: 2,
                            py: 1,
                            border: '1px solid #90caf9',
                            '&:hover': {
                              bgcolor: '#bbdefb',
                            },
                          }}
                        >
                          <UploadFileIcon color="primary" />
                          <Typography variant="body2" color="primary">
                            Click to upload files
                          </Typography>
                        </Box>
                        <input
                          id="file-upload"
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          multiple
                          onChange={handleFileChange}
                          disabled={loading}
                          style={{ display: 'none' }}
                        />
                      </label>

                      {/* Show existing files in edit mode */}
                      {isEdit && existingAttachments.length > 0 && (
                        <Stack spacing={1} sx={{ mt: 2 }}>
                          {existingAttachments.map((filename, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                                • {filename}
                              </Typography>
                              <IconButton size="small" onClick={() => handleRemoveExistingAttachment(idx)} disabled={loading}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Stack>
                      )}
                      {/* Show new files to upload */}
                      {attachments.length > 0 && (
                        <Stack spacing={1} sx={{ mt: 2 }}>
                          {attachments.map((file, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                                • {file.name}
                              </Typography>
                              <IconButton size="small" onClick={() => handleRemoveAttachment(idx)} disabled={loading}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
                      <Button
                        variant="outlined"
                        onClick={handleBackToList}
                        startIcon={<CloseIcon />}
                        disabled={loading}
                        size="large"
                      >
                        Close
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSaveClick}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        disabled={characterCount < minCharacters || loading}
                        size="large"
                        sx={{ minWidth: "160px" }}
                      >
                        {loading
                          ? (isEdit ? "Updating..." : "Saving...")
                          : (isEdit ? "Update Feedback" : "Save Feedback")}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Fade>
        </Container>
      </Box>
    </>
  );
}
export default JournalForm
